import { EvoluaApiError } from "@evolua/api-client";
import {
  Alert,
  Badge,
  Button,
  EmptyState,
  ErrorState,
  PageHeader,
  Skeleton,
  Table,
  badgeToneForStatus,
} from "@evolua/ui";
import { Link, useParams } from "react-router-dom";
import {
  useAckMeAnnouncement,
  useMeAnnouncement,
  useMeAnnouncements,
} from "../api/use-me.js";
import { useState } from "react";

function announcementLabel(status: string): string {
  if (status === "ACKED") return "Ciência";
  if (status === "READ") return "Lido";
  return "Não lido";
}

/** Comunicados do colaborador com confirmação de ciência (FASE-06-T52). */
export function ComunicadosPage() {
  const list = useMeAnnouncements();
  const correlationId =
    list.error instanceof EvoluaApiError ? list.error.problem.traceId : undefined;

  if (list.isPending) {
    return <Skeleton height="8rem" label="Comunicados" />;
  }

  if (list.isError || !list.data) {
    return (
      <ErrorState
        {...(correlationId ? { correlationId } : {})}
        onRetry={() => void list.refetch()}
      />
    );
  }

  return (
    <>
      <PageHeader
        title="Comunicados"
        lead={`Canal interno com confirmação de ciência. Pendentes: ${list.data.pendingAckCount}.`}
      />

      {list.data.data.length === 0 ? (
        <EmptyState
          title="Sem comunicados"
          description="Nenhum comunicado publicado para você no momento."
        />
      ) : (
        <Table
          caption="Comunicados"
          getRowId={(item) => item.id}
          columns={[
            {
              id: "title",
              header: "Título",
              cell: (item) => (
                <Link to={`/comunicados/${item.id}`}>
                  <strong>{item.title}</strong>
                </Link>
              ),
            },
            {
              id: "status",
              header: "Status",
              cell: (item) => (
                <Badge tone={badgeToneForStatus(item.status)}>
                  {announcementLabel(item.status)}
                </Badge>
              ),
            },
            {
              id: "preview",
              header: "Resumo",
              cell: (item) =>
                `${item.body.slice(0, 160)}${item.body.length > 160 ? "…" : ""}`,
            },
          ]}
          rows={list.data.data}
        />
      )}
    </>
  );
}

/** Detalhe + ciência. */
export function ComunicadoDetailPage() {
  const { id } = useParams<{ id: string }>();
  const detail = useMeAnnouncement(id ?? null);
  const ack = useAckMeAnnouncement();
  const [actionError, setActionError] = useState<string | null>(null);
  const correlationId =
    detail.error instanceof EvoluaApiError ? detail.error.problem.traceId : undefined;

  async function onAck() {
    if (!id) return;
    setActionError(null);
    try {
      await ack.mutateAsync(id);
      await detail.refetch();
    } catch (err) {
      setActionError(err instanceof Error ? err.message : "Falha ao confirmar");
    }
  }

  if (detail.isPending) {
    return <Skeleton height="8rem" label="Comunicado" />;
  }

  if (detail.isError || !detail.data) {
    return (
      <ErrorState
        {...(correlationId ? { correlationId } : {})}
        onRetry={() => void detail.refetch()}
      />
    );
  }

  const item = detail.data;

  return (
    <>
      <PageHeader
        title={item.title}
        lead={`Publicado em ${new Date(item.publishedAt).toLocaleString("pt-BR")}`}
        breadcrumbs={[
          { label: "Comunicados", href: "/comunicados" },
          { label: item.title },
        ]}
      />
      <Badge tone={badgeToneForStatus(item.status)}>
        {item.status === "ACKED"
          ? "Ciência confirmada"
          : item.status === "READ"
            ? "Lido"
            : "Não lido"}
      </Badge>
      <p>{item.body}</p>
      {actionError ? <Alert tone="danger">{actionError}</Alert> : null}
      {item.status !== "ACKED" ? (
        <Button
          type="button"
          className="evolua-mt"
          disabled={ack.isPending}
          onClick={() => void onAck()}
        >
          {ack.isPending ? "Confirmando…" : "Confirmar ciência"}
        </Button>
      ) : (
        <p className="evolua-muted">
          Ciência registrada em {new Date(item.ackedAt!).toLocaleString("pt-BR")}.
        </p>
      )}
    </>
  );
}
